import {
  extractRequiredParameters,
  getParameterFormat,
} from "./campaignTemplateUtils";

/** Parse template_json from an API template row. */
export function parseTemplateJson(template) {
  let json = template?.template_json || {};
  if (typeof json === "string") {
    try {
      json = JSON.parse(json);
    } catch {
      json = {};
    }
  }
  return json;
}

/** Lightweight snapshot stored on the node — avoids DB lookup at runtime. */
export function buildTemplateSnapshot(template) {
  const json = parseTemplateJson(template);
  return {
    name: json.name || template?.name || "",
    language: json.language || template?.language || "en_US",
    parameter_format: getParameterFormat(template),
    components: json.components || [],
  };
}

/** Build variable mapping rows from a selected template. */
export function buildVariableMappingsFromTemplate(template) {
  return extractRequiredParameters(template).map((param) => ({
    key: param.key,
    component: param.component,
    label: param.label,
    placeholder: param.placeholder,
    description: param.description,
    index: param.index,
    buttonText: param.buttonText,
    urlTemplate: param.urlTemplate,
    flowVariable: "",
  }));
}

/** QUICK_REPLY buttons become routable handles; URL/PHONE/COPY_CODE do not wait. */
export function buildRouteButtonsFromTemplate(template) {
  const json = parseTemplateJson(template);
  const buttonsComp = (json.components || []).find((c) => c.type === "BUTTONS");
  const buttons = buttonsComp?.buttons || [];

  return buttons
    .map((btn, index) => {
      if (btn.type !== "QUICK_REPLY") return null;
      return {
        id: `btn_${index}`,
        index,
        type: btn.type,
        text: btn.text || `Button ${index + 1}`,
        connectedNodeId: null,
      };
    })
    .filter(Boolean);
}

export function getHeaderMediaFormat(template) {
  const json = parseTemplateJson(template);
  const header = (json.components || []).find((c) => c.type === "HEADER");
  return header?.format?.toUpperCase() || "NONE";
}

export function hasQuickReplyButtons(buttons = []) {
  return buttons.some((b) => b.type === "QUICK_REPLY");
}

/** Resolve flow-variable mappings into send-time values (preview / validation). */
export function resolveMappingValues(variableMappings = [], flowVariables = []) {
  const known = new Set((flowVariables || []).map((v) => v.name));
  const values = {};

  for (const mapping of variableMappings) {
    const raw = (mapping.flowVariable || "").trim();
    if (!raw) {
      values[mapping.key] = "";
      continue;
    }
    const varName = raw.replace(/^\{\{|\}\}$/g, "").trim();
    values[mapping.key] = known.has(varName) ? `{{${varName}}}` : raw;
  }

  return values;
}

/** Apply selected template to node form state. */
export function applyTemplateSelection(template) {
  return {
    templateId: template.id,
    templateName: template.name,
    templateLanguage: template.language || "en_US",
    parameterFormat: getParameterFormat(template),
    templateSnapshot: buildTemplateSnapshot(template),
    variableMappings: buildVariableMappingsFromTemplate(template),
    buttons: buildRouteButtonsFromTemplate(template),
    headerMediaFormat: getHeaderMediaFormat(template),
    headerMediaOverride: "",
    selectionVariable: "",
  };
}
